import { useCallback, useEffect, useState } from 'react';
import { crearTercero, listarTerceros, suprimirTerceroRpc, type Tercero } from '../lib/api-catalogo';
import { useApp } from '../lib/contexto';
import { BarraDePagina, Boton, EncabezadoTabla, Esqueleto, EstadoVacio, FilaAcciones, FilaTabla, Insignia, TablaAncha, Tarjeta, TextoAyuda, ThAcciones } from '../ui/base';
import { FormularioTercero, type DatosFormularioTercero } from '../ui/FormularioTercero';
import { Modal } from '../ui/Modal';
import { instalacion } from '@tanteo/compartido/instalacion';

const URL_REGISTRO = instalacion.urls.registro;

export function Terceros() {
  const { negocio } = useApp();
  const esPropietario = negocio?.rol === 'propietario';
  const [terceros, setTerceros] = useState<Tercero[] | null>(null);
  const [creando, setCreando] = useState(false);
  const [qrAbierto, setQrAbierto] = useState(false);
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [suprimiendo, setSuprimiendo] = useState<Tercero | null>(null);
  const [aviso, setAviso] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const cargar = useCallback(() => {
    if (!negocio) return;
    listarTerceros(negocio.negocioId)
      .then(setTerceros)
      .catch(() => setTerceros([]));
  }, [negocio]);
  useEffect(cargar, [cargar]);

  async function abrirQr() {
    if (!negocio) return;
    const url = `${URL_REGISTRO}/?n=${negocio.negocioSlug}`;
    // qrcode se carga al usarse: solo lo necesita este modal, no el arranque.
    const { default: QRCode } = await import('qrcode');
    setQrDataUrl(await QRCode.toDataURL(url, { width: 280, margin: 1 }));
    setQrAbierto(true);
  }

  async function guardarNuevo(datos: DatosFormularioTercero) {
    if (!negocio) return;
    setOcupado(true);
    setAviso('');
    try {
      await crearTercero(negocio.negocioId, datos);
      setCreando(false);
      cargar();
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No se pudo crear.');
    } finally {
      setOcupado(false);
    }
  }

  async function suprimir() {
    if (!suprimiendo) return;
    setOcupado(true);
    try {
      await suprimirTerceroRpc(suprimiendo.id);
      setSuprimiendo(null);
      cargar();
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No se pudo suprimir.');
    } finally {
      setOcupado(false);
    }
  }

  if (!negocio) return null;

  return (
    <div className="space-y-4">
      <BarraDePagina titulo="Clientes y proveedores">
        {esPropietario && (
          <div className="flex gap-2">
            <Boton variante="secundario" onClick={() => void abrirQr()}>QR de auto-registro</Boton>
            <Boton onClick={() => setCreando(true)}>+ Tercero</Boton>
          </div>
        )}
      </BarraDePagina>

      <Tarjeta>
        {terceros === null ? (
          <Esqueleto lineas={6} />
        ) : terceros.length === 0 ? (
          <EstadoVacio titulo="Sin terceros registrados" detalle="Los clientes pueden auto-registrarse con el QR; los proveedores se crean solos al escanear el QR de tus compras." />
        ) : (
          <TablaAncha>
              <EncabezadoTabla>
                  <th className="py-2 pr-2">Documento</th>
                  <th className="py-2 pr-2">Nombre</th>
                  <th className="py-2 pr-2">Teléfono</th>
                  <th className="py-2 pr-2">Correo</th>
                  <th className="py-2 pr-2">Tipo</th>
                  <th className="py-2 pr-2">Origen</th>
                  {esPropietario && <ThAcciones className="w-24" />}
              </EncabezadoTabla>
              <tbody>
                {terceros.map((t) => (
                  <FilaTabla key={t.id}>
                    <td className="num py-2 pr-2">{t.tipoDocumento === '31' ? 'NIT ' : ''}{t.numeroDocumento}</td>
                    <td className="py-2 pr-2 font-semibold">{t.nombre}</td>
                    <td className="num py-2 pr-2">{t.telefono ?? '—'}</td>
                    {/* El comprobante se lo manda el negocio desde su propio correo: aquí está a mano. */}
                    <td className="py-2 pr-2 text-contenido">
                      {t.correo ? (
                        <a href={`mailto:${t.correo}`} className="text-info underline">
                          {t.correo}
                        </a>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="py-2 pr-2">
                      {t.esCliente && <Insignia tono="info">cliente</Insignia>}{' '}
                      {t.esProveedor && <Insignia tono="aviso">proveedor</Insignia>}
                    </td>
                    <td className="py-2 pr-2 text-contenido">{t.origen === 'auto_registro' ? `auto-registro${t.consentimiento ? ' ✓' : ''}` : t.origen}</td>
                    {esPropietario && (
                      <td className="py-2 text-right">
                        {t.numeroDocumento !== '222222222222' && (
                          <button className="text-destructive underline" onClick={() => setSuprimiendo(t)}>Suprimir</button>
                        )}
                      </td>
                    )}
                  </FilaTabla>
                ))}
              </tbody>
            </TablaAncha>
        )}
      </Tarjeta>

      <Modal abierto={qrAbierto} titulo="QR de auto-registro de clientes" onCerrar={() => setQrAbierto(false)}>
        <TextoAyuda>
          Imprime este QR y pégalo en el mostrador. El cliente lo escanea con SU celular, se registra y autoriza el
          tratamiento de sus datos (habeas data).
        </TextoAyuda>
        {qrDataUrl && <img src={qrDataUrl} alt="QR de registro" className="mx-auto" />}
        <p className="num mt-2 text-center text-etiqueta">{`${URL_REGISTRO}/?n=${negocio.negocioSlug}`}</p>
      </Modal>

      <Modal abierto={creando} titulo="Nuevo tercero" onCerrar={() => setCreando(false)}>
        <FormularioTercero
          contexto="cliente"
          mostrarRoles
          ocupado={ocupado}
          errorServidor={aviso}
          onGuardar={(d) => void guardarNuevo(d)}
          onCancelar={() => setCreando(false)}
        />
      </Modal>

      <Modal abierto={suprimiendo !== null} titulo="Suprimir datos del tercero" onCerrar={() => setSuprimiendo(null)}>
        <p className="text-muted mb-4 text-contenido">
          Supresión por habeas data: el nombre, documento y teléfono de <strong>{suprimiendo?.nombre}</strong> se
          anonimizan de forma permanente. Sus compras históricas se conservan sin datos personales.
        </p>
        <FilaAcciones>
          <Boton variante="contorno" onClick={() => setSuprimiendo(null)} disabled={ocupado}>Volver</Boton>
          <Boton variante="peligro" onClick={() => void suprimir()} disabled={ocupado}>Suprimir definitivamente</Boton>
        </FilaAcciones>
      </Modal>
    </div>
  );
}
